import type { TOC } from '@ember/component/template-only'
import type { WithBoundArgs } from '@glint/template'
import { hash, get } from '@ember/helper'
import { or } from 'ember-truth-helpers'
import { BasicTableHeadCell } from 'teacher-ui-client/components/stable-table/basic-table/head-cell'
import {
  BasicTableCell,
  type CellObject,
  type ColumnObject,
  type RowObject,
} from 'teacher-ui-client/components/stable-table/basic-table/cell'
import type { Config } from 'teacher-ui-client/components/stable-table/basic-table'

function emptyObjectArray(length = 0): object[] {
  return Array.from({ length }, () => ({}))
}

interface RowSignature {
  Element: HTMLTableRowElement
  Args: {
    isHeader?: boolean
    columns: ColumnObject[]
    rowObject: RowObject
    rowIndex?: number
    config?: Config
  }
  Blocks: {
    default: [
      {
        cell: WithBoundArgs<
          typeof BasicTableCell | typeof BasicTableHeadCell,
          'cellObject' | 'columnObject' | 'rowObject' | 'cellIndex' | 'rowIndex' | 'config'
        >
      },
      { cellObject: CellObject; cellIndex: number; columnObject: ColumnObject },
    ]
  }
}

/**
 * Used by either body or head components. isHeader=true if within head component
 */
export const BasicTableRow: TOC<RowSignature> = <template>
  <tr data-test-basic-table-row={{if @isHeader true @rowIndex}} class={{@rowObject.classNames}} ...attributes>
    {{#each (or @rowObject.cells (emptyObjectArray @columns.length)) as |cellObject index|}}
      {{#let (get @columns index) as |columnObject|}}
        {{! Use the head-cell component if this component is used within head component }}
        {{#let
          (if
            @isHeader
            (component
              BasicTableHeadCell
              cellObject=cellObject
              columnObject=columnObject
              rowObject=@rowObject
              cellIndex=index
              rowIndex=@rowIndex
              config=@config
            )
            (component
              BasicTableCell
              cellObject=cellObject
              columnObject=columnObject
              rowObject=@rowObject
              cellIndex=index
              rowIndex=@rowIndex
              config=@config
            )
          )
          as |cell|
        }}
          {{#if (has-block)}}
            {{yield (hash cell=cell) (hash cellObject=cellObject cellIndex=index columnObject=columnObject)}}
          {{else}}
            {{cell}}
          {{/if}}
        {{/let}}
      {{/let}}
    {{/each}}
  </tr>
</template>

export default BasicTableRow
